import React, {useEffect, useRef} from "react";
import {AppLayout} from "../layout/AppLayout";
import {useParams} from "react-router-dom";
import {Form} from "../ui/Form";
import {FormGroup} from "../ui/FormGroup";
import {FormInput} from "../ui/FormInput";
import {Card} from "../ui/Card";
import {PrimaryButton} from "../ui/Button";
import {CompanyService} from "../../services/CompanyService";
import {Company} from "../../models/Company";
import {useForceUpdate} from "../../hooks/useForceUpdate";
import {useRouter} from "../../hooks/useRouter";
import {NotificationInvoker} from "../../hooks/useNotificationState";
import {yup} from "../../extension/yup";
import {CountrySelect} from "../ui/async-selects/CountrySelect";


interface Props {

}

const Schema = yup.object().shape({
    name: yup.string().required(),
    tax_number: yup.string(),
    email: yup.string().email().required(),
    phone: yup.string(),
    address: yup.string(),
    city: yup.string(),
    country: yup.string(),
    website: yup.string(),
})

export const CompanyDetail: React.FC<Props> = () => {

    const params = useParams<"id">();
    const router = useRouter();
    const payload = useRef<Partial<Company>>({

    });

    const forceUpdate = useForceUpdate();

    const isCreating = params.id === "new";

    const hydrate = async (id: string | undefined) => {
        if (isCreating || !id) {
            return;
        }

        try {
            const company = await new CompanyService().get(id);

            payload.current = {
                name: company.name,
                tax_number: company.tax_number,
                email: company.email,
                phone: company.phone,
                address: company.address,
                city: company.city,
                country: company.country,
                website: company.website
            }
            forceUpdate();
        } catch (e) {
            router.to("/companies");
        }
    }

    useEffect(() => {
        hydrate(params.id);
    }, [params.id]);

    const store = async (data: Partial<Company>) => {
        if (!isCreating) {
            await new CompanyService().update(params.id!, data);
            NotificationInvoker("success", "Erfolgreich", "Unternehmen erfolgreich gespeichert");
            router.to("/companies");
        } else {
            await new CompanyService().create(data);
            NotificationInvoker("success", "Erfolgreich", "Unternehmen erfolgreich gespeichert");
            router.to("/companies");
        }
    }

    return <AppLayout title={isCreating
            ? "Unternehmen / Neu"
            : `Unternehmen / ${params.id}`
        }>
            <Card>
                <Form initialValues={payload.current}
                      enableReinitialize={true}
                      onSubmit={async (data, helpers) => {
                          try {
                              await store(data)
                          } catch (e: any) {
                              e.validation && helpers.setErrors(e.validation);
                              NotificationInvoker("error", "Fehlgeschlagen", "Aktion konnte nicht durchgeführt werden");
                          }
                      }}
                      validationSchema={Schema}
                >
                    <div className="space-y-6">
                        <FormGroup name={"name"} title={"Name"}>
                            <FormInput name={"name"} type={"text"} />
                        </FormGroup>

                        <FormGroup name={"tax_number"} title={"Steuernummer"}>
                            <FormInput name={"tax_number"} type={"text"} />
                        </FormGroup>

                        <FormGroup name={"email"} title={"E-Mail"}>
                            <FormInput name={"email"} type={"text"} />
                        </FormGroup>

                        <FormGroup name={"phone"} title={"Telefon"}>
                            <FormInput name={"phone"} type={"text"} />
                        </FormGroup>

                        <div className="grid md:grid-cols-3 gap-4">
                            <FormGroup name={"address"} title={"Addresse"}>
                                <FormInput name={"address"} type={"text"} />
                            </FormGroup>

                            <FormGroup name={"city"} title={"Stadt"}>
                                <FormInput name={"city"} type={"text"} />
                            </FormGroup>

                            <FormGroup name={"country"} title={"Land"}>
                                <CountrySelect name={"country"} />
                            </FormGroup>
                        </div>

                        <FormGroup name={"website"} title={"Webseite"}>
                            <FormInput name={"website"} type={"text"} />
                        </FormGroup>


                        <PrimaryButton size={"lg"} type={"submit"}>
                            {isCreating && "Erstellen"}
                            {!isCreating && "Speichern"}
                        </PrimaryButton>
                    </div>
                </Form>
            </Card>
        </AppLayout>
};